import { useCallback, useEffect, useState } from 'react';

import downloadFile from '@lyearn/core/utils/network/downloadFile';
import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleFill, LoaderFill } from '@lyearn/icons';
import { VideoDownloadProps } from '@lyearn/lygo-core';
import { LinkButton, useSnackbar } from '@lyearn/ui';
import { EXPORT_PROGRESS_MAP } from '@/modules/corporate/components/BulkExport/config';
import useExportJobMutation from '@/modules/corporate/components/BulkExport/hooks/useExportJobMutation';
import useExportProps from '@/modules/corporate/components/BulkExport/hooks/useExportProps';
import { JobEntityStatusEnum, JobPurposeEnum } from '@/types/schema';

const purpose = JobPurposeEnum.DownloadVideo;

const emptyFn = () => undefined;

const useVideoDownload = () => {
  const { t } = useTranslation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { onExport } = useExportJobMutation({ purpose });
  const { setCurrentExportJob, job, setJob, currentExportJob } = useExportProps({
    purpose: [purpose],
    getLatestJob: emptyFn,
  });
  const [videoDownloadProps, setVideoDownloadProps] = useState<VideoDownloadProps>();

  const onDownloadUrl = useCallback(
    (url: string) => {
      if (url) {
        downloadFile(url);
        showSnackbar({ message: t('Course:VideoDownloadSuccessMessage'), iconStatus: 'success' });
      }
    },
    [showSnackbar, t],
  );

  const onVideoDownload = useCallback(
    async (props: VideoDownloadProps) => {
      setVideoDownloadProps(props);
      if (props.downloadableUrl) {
        onDownloadUrl(props.downloadableUrl);
        setVideoDownloadProps(undefined);
      } else if (props.downloadVideoInput && !currentExportJob?.id) {
        const res = await onExport({
          downloadVideoInput: props.downloadVideoInput,
        });

        if (res.error) {
          showErrorSnackbar({
            startIcon: <AlertTriangleFill className="w-20 h-20" fillColorToken="icon-white" />,
            message: t('Course:VideoDownloadErrorMessage'),
            action: (
              <LinkButton
                className="flex-shrink-0 py-2 px-4"
                color="inverse"
                onClick={() => onVideoDownload(props)}>
                {t('Common:Actions.TryAgain')}
              </LinkButton>
            ),
          });
        } else if (res.data?.createImportJob?.jobScheduleId) {
          setCurrentExportJob({ id: res.data.createImportJob.jobScheduleId });
        }
      }
    },
    [currentExportJob?.id, onDownloadUrl, onExport, setCurrentExportJob, showErrorSnackbar, t],
  );

  useEffect(() => {
    const progress = job?.status ? EXPORT_PROGRESS_MAP[job.status] : null;

    if (progress === null) return;

    if (job?.status === JobEntityStatusEnum.Failed) {
      showErrorSnackbar({
        startIcon: <AlertTriangleFill className="w-20 h-20" fillColorToken="icon-white" />,
        message: t('Course:VideoDownloadErrorMessage'),
        action: videoDownloadProps ? (
          <LinkButton
            className="flex-shrink-0 py-2 px-4"
            color="inverse"
            onClick={() => onVideoDownload(videoDownloadProps)}>
            {t('Common:Actions.TryAgain')}
          </LinkButton>
        ) : null,
      });
      setCurrentExportJob(undefined);
    } else if (progress >= 0 && progress < 100) {
      showSnackbar({
        StartIcon: LoaderFill,
        message: t('Course:VideoDownloadInprogressMessage'),
        iconStatus: 'info',
      });
    } else if (progress === 100 && job?.outputMediaUrls.length) {
      const outputMediaUrl = job.outputMediaUrls[0].url;
      outputMediaUrl && onDownloadUrl(outputMediaUrl);
      setCurrentExportJob(undefined);
      setJob(undefined);
      setVideoDownloadProps(undefined);
    }
  }, [
    job?.outputMediaUrls,
    job?.status,
    onVideoDownload,
    onDownloadUrl,
    setCurrentExportJob,
    showErrorSnackbar,
    showSnackbar,
    t,
    videoDownloadProps,
    currentExportJob,
    setJob,
  ]);

  return {
    onVideoDownload,
  };
};

export default useVideoDownload;
